import { UserFriendlyNoteFailedReason } from '@heidi/schema/ml-scribe';

/**
 * Known note-generation stream `detail.type` / `error.message` codes.
 * Stream `error.message === …` branches and {@link isNoteFailedErrorCode}
 * both derive from this object.
 */
export const NoteGenerationErrorCode = {
  UnlimitedQuotaUntilNotSet: 'UnlimitedQuotaUntilNotSetError',
  AskAiQuotaExceed: 'ASKAIQuotaExceedError',
  TemplateQuotaExceed: 'TemplateQuotaExceedError',
  TeamTemplateAccessDenied: 'TeamTemplateAccessDeniedError',
  GenerationTemplateAccessDenied: 'GenerationTemplateAccessDeniedError',
  TemplateDeleted: 'TemplateDeletedError',
  TemplateNotFound: 'TemplateNotFoundError',
} as const;

export type NoteGenerationErrorCode =
  (typeof NoteGenerationErrorCode)[keyof typeof NoteGenerationErrorCode];

export const KNOWN_NOTE_GENERATION_ERROR_CODES = Object.values(
  NoteGenerationErrorCode
) as readonly NoteGenerationErrorCode[];

export const NoteFailedClientErrorCode = {
  TypeError: 'TypeError',
  AbortError: 'AbortError',
  NetworkError: 'NetworkError',
  EmptyNoteStream: 'empty_note',
  Unknown: 'unknown',
} as const;

export type NoteFailedClientErrorCode =
  (typeof NoteFailedClientErrorCode)[keyof typeof NoteFailedClientErrorCode];

/** Honest regenerate-path categories. ASKAI vs template is already collapsed upstream. */
export const NoteFailedSourceErrorCode = {
  QuotaExceeded: 'quota_exceeded',
  TranscriptDeleted: 'transcript_deleted',
  SessionNotTranscribed: 'session_not_transcribed',
} as const;

export type NoteFailedSourceErrorCode =
  (typeof NoteFailedSourceErrorCode)[keyof typeof NoteFailedSourceErrorCode];

/**
 * Group-by codes for `note_failed.error_code`. Closed vocabulary.
 * Unlisted backend `detail.type` values parse to `unknown`.
 */
export type NoteFailedErrorCode =
  | NoteGenerationErrorCode
  | UserFriendlyNoteFailedReason
  | NoteFailedClientErrorCode
  | NoteFailedSourceErrorCode
  | `HTTP_${number}`;

const HTTP_CODE = /^HTTP_\d{3}$/;
const BACKEND_NOTE_FAILED_REASONS = Object.values(UserFriendlyNoteFailedReason);

export function isNoteFailedErrorCode(value: string): value is NoteFailedErrorCode {
  if ((KNOWN_NOTE_GENERATION_ERROR_CODES as readonly string[]).includes(value)) {
    return true;
  }
  if ((BACKEND_NOTE_FAILED_REASONS as readonly string[]).includes(value)) {
    return true;
  }
  if ((Object.values(NoteFailedClientErrorCode) as readonly string[]).includes(value)) {
    return true;
  }
  if ((Object.values(NoteFailedSourceErrorCode) as readonly string[]).includes(value)) {
    return true;
  }
  return HTTP_CODE.test(value);
}
